import { GraphQLQuery, GraphQLResponse } from './schema';

/**
 * Simple GraphQL client for making queries to the GraphQL API
 */
export class GraphQLClient {
  private endpoint: string;

  constructor(endpoint: string = '/api/graphql') {
    this.endpoint = endpoint;
  }

  /**
   * Execute a GraphQL query
   */
  async query<T = unknown>(query: string, variables?: Record<string, unknown>): Promise<T> {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          variables,
        } as GraphQLQuery),
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }

      const result: GraphQLResponse = await response.json();

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors.map(e => e.message).join(', '));
      }

      return result.data as T;
    } catch (error) {
      console.error('[GraphQL Client] Error executing query:', error);
      throw error;
    }
  }

  /**
   * Execute a GraphQL query using GET (for simple queries)
   */
  async queryGet<T = unknown>(
    operation: 'media' | 'sectionMedia' | 'pageMedia' | 'pageMediaOptimized' | 'allPagesMedia',
    params: Record<string, string>
  ): Promise<T> {
    try {
      const searchParams = new URLSearchParams({
        operation,
        ...params,
      });

      const response = await fetch(`${this.endpoint}?${searchParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.statusText}`);
      }

      const result: GraphQLResponse = await response.json();

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors.map(e => e.message).join(', '));
      }

      return result.data as T;
    } catch (error) {
      console.error('[GraphQL Client] Error executing GET query:', error);
      throw error;
    }
  }
}

// Default client instance
export const graphqlClient = new GraphQLClient();

